"use client"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import {
  EXPENSE_BAR_CLASS,
  EXPENSE_TEXT_CLASS,
  INCOME_BAR_CLASS,
  INCOME_TEXT_CLASS,
  TrackBar,
} from "@/features/finance/overview/church-f3/components/church-tile-parts"
import type { ActivityBar } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { cn } from "@ibada/ui"
import { FALLBACK_MUTED_CLASS, type TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"


/** How many activities fit the shorter third row; the rest are counted. */
const ACTIVITIES_VISIBLE = 4

/**
 * Activities that cost (F3): the net of each activity across every branch, the ones that cost the church first in
 * rose, then those that raised money in teal, all on the scale of the largest.
 */
export function ChurchActivitiesTile({
  rows,
  currency,
  fallback = null,
}: {
  rows: readonly ActivityBar[]
  currency: string
  /** Set when the tile shows the previous period because this one has no money yet: its label, bars muted. */
  fallback?: TileFallback | null
}) {
  const t = useTranslations("finance.overview")
  const visible = rows.slice(0, ACTIVITIES_VISIBLE)

  return (
    <HomeTile
      title={t("widgets.churchActivities.title")}
      subtitle={fallback?.label ?? t("widgets.churchActivities.subtitle")}
      testId='church-tile-activities'
    >
      {rows.length === 0 ? (
        <HomeTileEmpty message={t("church.activities.empty")} />
      ) : (
        <ul data-fallback={fallback ? true : undefined} className={cn("flex flex-col gap-2", fallback && FALLBACK_MUTED_CLASS)}>
          {visible.map((row) => (
            <li key={row.name} data-testid='church-activity-row' className='flex flex-col gap-1'>
              <span className='flex gap-2 text-[12.5px]'>
                <span className='truncate text-muted-foreground'>{row.name}</span>
                <span
                  className={cn(
                    "ml-auto font-bold whitespace-nowrap tabular-nums",
                    row.netIsNegative ? EXPENSE_TEXT_CLASS : INCOME_TEXT_CLASS,
                  )}
                >
                  {row.netIsNegative ? "" : "+"}
                  {formatCurrency(row.net, currency)}
                </span>
              </span>
              <TrackBar ratio={row.ratio} className={row.netIsNegative ? EXPENSE_BAR_CLASS : INCOME_BAR_CLASS} />
            </li>
          ))}
          {rows.length > ACTIVITIES_VISIBLE ? (
            <li className='text-xs text-muted-foreground'>{t(rows.length - ACTIVITIES_VISIBLE === 1 ? "church.activities.moreOne" : "church.activities.moreOther", {
                count: rows.length - ACTIVITIES_VISIBLE,
              })}</li>
          ) : null}
        </ul>
      )}
    </HomeTile>
  )
}
